import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { Box, Button } from "@mui/material";
import { ComposableMap, Geographies, ZoomableGroup } from "react-simple-maps";
import ReactTooltip from "react-tooltip";
import * as d3 from "d3";
import CloseIcon from "@mui/icons-material/Close";
import { useStyles, tooltipBkgColor } from "../shared/MapTooltip";
import { ShortFormat } from "../shared/ConvertionFormats";
import DrawLegend from "../shared/DrawLegend";
import { getCountyPercentiles } from "../../utils/countyLegendConfig";
import { buildCountyColorScale, getSparseCountyEntries } from "../../utils/countyColorScale";
import SparseCountyLegend from "../shared/countyMap/SparseCountyLegend";
import { CountyGeographyLayer, StateBoundaryLayer, StateLabelLayer } from "../shared/countyMap/CountyMapLayers";
import MapLoadingOverlay from "../shared/MapLoadingOverlay";
import {
    COUNTY_TOPOJSON_URL,
    STATE_TOPOJSON_URL,
    clampCountyMapCenter,
    getCountyMapPosition,
    getStateFipsFromName,
    getStateViewport,
    loadCountyAndStateTopoJson,
    normalizeCountyFips
} from "../../utils/countyGeo";
import CountyBreakdownTables from "./cropSelection/CountyBreakdownTables";
import SelectionTitle from "../shared/SelectionTitle";
import {
    AVERAGE_BASIS_NOTE,
    HighlightPill,
    LOSS_RATIO_NOTE,
    LOSS_RATIO_THRESHOLDS,
    NetFarmerBenefitNote,
    PrfAcresCaption,
    getLossRatioColors
} from "./cropInsuranceConstants";
import { ALL_CROPS_SENTINEL } from "./cropSelection/commodityMapping";
import { BRAND_GREEN, BRAND_GREEN_10, BRAND_GREEN_50, BRAND_GREEN_90, TEXT_FAINT, WHITE_90 } from "../shared/colors";

const lossRatioThresholds = LOSS_RATIO_THRESHOLDS;

const getValueFromAttrDollar = (record: any, attribute: string): string => {
    let ans = "";
    Object.keys(record).forEach((key) => {
        const match = key.match(/^(.*?)(?=\s*InDollars)/);
        const extractedKey = match ? match[1] : key;
        if (extractedKey === attribute) {
            ans = key;
        }
    });
    return ans;
};

const countyAttributeKeyMap: Record<string, string> = {
    totalNetFarmerBenefit: "totalNetFarmerBenefitInDollars",
    totalIndemnities: "totalIndemnitiesInDollars",
    totalPremium: "totalPremiumInDollars",
    totalPremiumSubsidy: "totalPremiumSubsidyInDollars",
    totalFarmerPaidPremium: "totalFarmerPaidPremiumInDollars",
    averageLiabilities: "averageLiabilitiesInDollars",
    averageInsuredAreaInAcres: "averageInsuredAreaInAcres",
    totalPoliciesEarningPremium: "totalPoliciesEarningPremium",
    lossRatio: "lossRatio"
};

const resolveCountyAttributeKey = (record: any, attribute: string): string => {
    const mappedKey = countyAttributeKeyMap[attribute];
    if (mappedKey && Object.prototype.hasOwnProperty.call(record, mappedKey)) {
        return mappedKey;
    }
    const matchedKey = getValueFromAttrDollar(record, attribute);
    return matchedKey !== "" ? matchedKey : attribute;
};

interface CropInsuranceCountyMapProps {
    attribute: string;
    year: string;
    mapColor: [string, string, string, string, string];
    countyPerformance: any;
    stateCodes: Record<string, string>;
    allStates: any[];
    selectedState: string;
    onStateChange: (state: string) => void;
    selectedCrops?: string[];
    yearKeys?: string[];
    metricLabel?: string;
}

const CropInsuranceCountyMap = ({
    attribute,
    year,
    mapColor,
    countyPerformance,
    stateCodes,
    allStates,
    selectedState,
    onStateChange,
    selectedCrops = [],
    yearKeys = [],
    metricLabel = ""
}: CropInsuranceCountyMapProps): JSX.Element => {
    const classes = useStyles();
    const [content, setContent] = useState<React.ReactNode>("");
    const [position, setPosition] = useState(getCountyMapPosition("All States", 1));
    const [userZoomLevel, setUserZoomLevel] = useState(1);
    const [countyTopoReady, setCountyTopoReady] = useState(false);
    const [stateTopoReady, setStateTopoReady] = useState(false);
    const [countyTopology, setCountyTopology] = useState<Record<string, unknown> | null>(null);
    const [stateTopology, setStateTopology] = useState<Record<string, unknown> | null>(null);
    const [topologyLoadAttempted, setTopologyLoadAttempted] = useState(false);
    const [mapDrawSettled, setMapDrawSettled] = useState(false);
    const [interactionReady, setInteractionReady] = useState(false);
    const mountedRef = useRef(true);
    const hoveredCountyRef = useRef<string | null>(null);
    const tooltipFrameRef = useRef<number | null>(null);
    const pendingTooltipContentRef = useRef<React.ReactNode>("");
    const { countyDataMap, countyValueMap, quantizeArray, zeroPoints } = useMemo(() => {
        const map: Record<string, any> = {};
        const valueMap: Record<string, number> = {};
        const qArray: number[] = [];
        const zPoints: string[] = [];
        if (countyPerformance && countyPerformance[year]) {
            countyPerformance[year].forEach((county: any) => {
                const stateName = stateCodes[county.state] || county.state;
                if (selectedState !== "All States" && stateName !== selectedState) {
                    return;
                }
                const countyFips = normalizeCountyFips(county.countyFips);
                if (!countyFips) {
                    return;
                }
                map[countyFips] = county;
                const key = resolveCountyAttributeKey(county, attribute);
                const value = Number(county[key]);
                if (!Number.isFinite(value)) {
                    return;
                }
                valueMap[countyFips] = value;
                qArray.push(value);
                if (value === 0) {
                    zPoints.push(countyFips);
                }
            });
        }
        return { countyDataMap: map, countyValueMap: valueMap, quantizeArray: qArray, zeroPoints: zPoints };
    }, [countyPerformance, year, attribute, selectedState, stateCodes]);

    const countyPercentiles = getCountyPercentiles(attribute);

    const isLossRatio = attribute === "lossRatio";
    const isCountAttribute = attribute === "averageInsuredAreaInAcres" || attribute === "totalPoliciesEarningPremium";
    const lossRatioColors = getLossRatioColors(mapColor);
    const quantileColorScale = useMemo(
        () => buildCountyColorScale(quantizeArray, countyPercentiles, mapColor),
        [quantizeArray, countyPercentiles, mapColor]
    );
    const mode = isLossRatio ? "quantile" : quantileColorScale.mode;
    const colorScale = isLossRatio
        ? d3.scaleThreshold(lossRatioThresholds, lossRatioColors)
        : quantileColorScale.colorScale;
    const countyGeographySource = countyTopology || (topologyLoadAttempted ? COUNTY_TOPOJSON_URL : null);
    const stateGeographySource = stateTopology || (topologyLoadAttempted ? STATE_TOPOJSON_URL : null);
    const mapIsReady =
        countyGeographySource !== null &&
        stateGeographySource !== null &&
        countyTopoReady &&
        stateTopoReady &&
        mapDrawSettled;
    const selectedStateFips = selectedState === "All States" ? null : getStateFipsFromName(selectedState);

    const stateAbbrevByVal = useMemo(() => {
        const mapped: Record<string, string> = {};
        allStates.forEach((state: any) => {
            if (state?.val !== undefined && state?.id) {
                mapped[String(state.val)] = state.id;
            }
        });
        return mapped;
    }, [allStates]);

    let attr = 0;
    if (attribute === "lossRatio") attr = 1;
    if (attribute === "averageInsuredAreaInAcres" || attribute === "totalPoliciesEarningPremium") attr = 2;

    useEffect(() => {
        mountedRef.current = true;
        loadCountyAndStateTopoJson()
            .then(([countyTopo, stateTopo]) => {
                if (!mountedRef.current) {
                    return;
                }
                setCountyTopology(countyTopo);
                setStateTopology(stateTopo);
            })
            .catch(() => undefined)
            .finally(() => {
                if (mountedRef.current) {
                    setTopologyLoadAttempted(true);
                }
            });

        return () => {
            mountedRef.current = false;
            if (tooltipFrameRef.current !== null) {
                window.cancelAnimationFrame(tooltipFrameRef.current);
            }
        };
    }, []);

    useEffect(() => {
        setMapDrawSettled(false);
        setInteractionReady(false);
        const timer = setTimeout(() => {
            if (mountedRef.current) {
                setMapDrawSettled(true);
            }
        }, 220);
        return () => {
            clearTimeout(timer);
        };
    }, [selectedState, attribute, year]);

    useEffect(() => {
        if (!mapIsReady) {
            setInteractionReady(false);
            return undefined;
        }

        let rafA: number | null = null;
        let rafB: number | null = null;
        let idleId: number | null = null;
        let fallbackTimeoutId: number | null = null;
        let finished = false;

        const finish = () => {
            if (finished || !mountedRef.current) return;
            finished = true;
            if (fallbackTimeoutId !== null) {
                clearTimeout(fallbackTimeoutId);
            }
            setInteractionReady(true);
        };

        fallbackTimeoutId = window.setTimeout(finish, 1200);
        const scheduleViaRaf = () => {
            rafA = window.requestAnimationFrame(() => {
                rafB = window.requestAnimationFrame(finish);
            });
        };
        const windowWithIdle = window as Window & {
            requestIdleCallback?: (cb: IdleRequestCallback, options?: IdleRequestOptions) => number;
            cancelIdleCallback?: (handle: number) => void;
        };

        // Fallback to double requestAnimationFrame for a similar effect if requestIdleCallback is not supported.
        if (typeof windowWithIdle.requestIdleCallback === "function") {
            idleId = windowWithIdle.requestIdleCallback(
                () => {
                    scheduleViaRaf();
                },
                { timeout: 900 }
            );
        } else {
            scheduleViaRaf();
        }

        // Noticed that in some cases the pop-up of the county map still need extra time to make sure the mouse transition is smooth without getting stuck,
        // So extra loading buffer in case the county map is still not fully interactive after the above timers
        return () => {
            if (rafA !== null) {
                window.cancelAnimationFrame(rafA);
            }
            if (rafB !== null) {
                window.cancelAnimationFrame(rafB);
            }
            if (idleId !== null && typeof windowWithIdle.cancelIdleCallback === "function") {
                windowWithIdle.cancelIdleCallback(idleId);
            }
            if (fallbackTimeoutId !== null) {
                clearTimeout(fallbackTimeoutId);
            }
        };
    }, [mapIsReady]);

    const getBasePosition = useCallback(
        (zoomLevel: number) => getCountyMapPosition(selectedState, zoomLevel),
        [selectedState]
    );

    useEffect(() => {
        setPosition(getBasePosition(userZoomLevel));
    }, [getBasePosition, userZoomLevel]);

    const handleCountyTopoReady = useCallback(() => {
        if (mountedRef.current) {
            setCountyTopoReady(true);
        }
    }, []);

    const handleStateTopoReady = useCallback(() => {
        if (mountedRef.current) {
            setStateTopoReady(true);
        }
    }, []);

    const scheduleTooltipContent = useCallback((nextContent: React.ReactNode) => {
        pendingTooltipContentRef.current = nextContent;
        if (tooltipFrameRef.current !== null) {
            return;
        }
        tooltipFrameRef.current = window.requestAnimationFrame(() => {
            tooltipFrameRef.current = null;
            setContent(pendingTooltipContentRef.current);
        });
    }, []);

    const handleZoomIn = useCallback(() => {
        if (!mountedRef.current) return;
        const newZoomLevel = Math.min(userZoomLevel * 1.2, 3);
        setUserZoomLevel(newZoomLevel);
    }, [userZoomLevel]);

    const handleZoomOut = useCallback(() => {
        if (!mountedRef.current) return;
        const newZoomLevel = Math.max(userZoomLevel / 1.2, 0.5);
        setUserZoomLevel(newZoomLevel);
    }, [userZoomLevel]);

    const handleResetZoom = useCallback(() => {
        if (!mountedRef.current) return;
        setUserZoomLevel(1);
        setPosition(getBasePosition(1));
    }, [getBasePosition]);

    const handleCloseStateView = useCallback(() => {
        if (mountedRef.current) {
            onStateChange("All States");
            setUserZoomLevel(1);
            setPosition(getCountyMapPosition("All States", 1));
        }
    }, [onStateChange]);

    const handleMoveEnd = useCallback(
        (positionObj: { coordinates: number[]; zoom: number }) => {
            if (!mountedRef.current) return;
            if (selectedState === "All States") {
                const clamped = clampCountyMapCenter(positionObj.coordinates);
                setPosition({ coordinates: clamped, zoom: positionObj.zoom });
            } else {
                const stateView = getStateViewport(selectedState);
                if (stateView) {
                    const [centerLon, centerLat] = stateView.center;
                    if (positionObj.coordinates[0] !== centerLon || positionObj.coordinates[1] !== centerLat) {
                        setPosition({ coordinates: stateView.center, zoom: positionObj.zoom });
                    }
                }
            }
        },
        [selectedState]
    );

    const getCountyFillColor = useCallback(
        (countyFIPS: string) => {
            const value = countyValueMap[countyFIPS];
            if (value === undefined) return "#EEE";
            if (value === 0) return TEXT_FAINT;
            return colorScale(value);
        },
        [countyValueMap, colorScale]
    );

    const handleMouseEnter = useCallback(
        (geo: any, countyFIPS: string) => {
            if (hoveredCountyRef.current === countyFIPS) {
                return;
            }
            hoveredCountyRef.current = countyFIPS;
            const countyData = countyDataMap[countyFIPS];
            if (!countyData) {
                const tooltipContent = (
                    <div className="map_tooltip">
                        <div className={classes.tooltip_overall}>
                            <div className={classes.tooltip_header}>
                                <b>{geo.properties?.name || "Unknown County"}</b>
                            </div>
                            <table className={classes.tooltip_table}>
                                <tbody>
                                    <tr>
                                        <td className={classes.tooltip_topcell_left}>No data available</td>
                                        <td className={classes.tooltip_topcell_right}>&nbsp;</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                        <div style={{ height: "8px", backgroundColor: tooltipBkgColor }} />
                    </div>
                );
                scheduleTooltipContent(tooltipContent);
                return;
            }

            const value = countyValueMap[countyFIPS] || 0;
            const stateName = stateCodes[countyData.state] || countyData.state;

            const tooltipContent = (
                <div className="map_tooltip">
                    <div className={classes.tooltip_overall}>
                        <div className={classes.tooltip_header}>
                            <b>
                                {countyData.countyName}, {stateName}
                            </b>
                        </div>
                        {attr === 1 ? (
                            <table className={classes.tooltip_table}>
                                <tbody>
                                    <tr>
                                        <td className={classes.tooltip_topcell_left}>
                                            {Number(value).toLocaleString(undefined, { maximumFractionDigits: 2 })}
                                        </td>
                                        <td className={classes.tooltip_topcell_right}>&nbsp;</td>
                                    </tr>
                                </tbody>
                            </table>
                        ) : (
                            <table className={classes.tooltip_table}>
                                <tbody>
                                    {attr === 2 ? (
                                        <tr>
                                            <td className={classes.tooltip_topcell_left}>{ShortFormat(value)}</td>
                                            <td className={classes.tooltip_topcell_right}>&nbsp;</td>
                                        </tr>
                                    ) : (
                                        <tr>
                                            <td className={classes.tooltip_topcell_left}>${ShortFormat(value)}</td>
                                            <td className={classes.tooltip_topcell_right}>&nbsp;</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        )}
                        <CountyBreakdownTables
                            record={countyData}
                            attribute={attribute}
                            metricLabel={metricLabel}
                            yearKeys={yearKeys}
                            selectedCrops={selectedCrops}
                        />
                    </div>
                    <div style={{ height: "8px", backgroundColor: tooltipBkgColor }} />
                </div>
            );
            scheduleTooltipContent(tooltipContent);
        },
        [
            countyDataMap,
            countyValueMap,
            stateCodes,
            classes,
            attr,
            scheduleTooltipContent,
            attribute,
            metricLabel,
            yearKeys,
            selectedCrops
        ]
    );

    const handleMouseLeave = useCallback(() => {
        hoveredCountyRef.current = null;
        scheduleTooltipContent("");
    }, [scheduleTooltipContent]);

    const titleElement = (): JSX.Element => {
        const displayAttribute = attribute
            .replace(/([A-Z])/g, " $1")
            .trim()
            .split(" ")
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(" ");
        const titleMetricLabel = (metricLabel || displayAttribute).replace(/^Average\s+/i, "");
        const selectionTitle = (
            <SelectionTitle
                metricLabel={titleMetricLabel}
                selectedYears={yearKeys.length > 0 ? yearKeys : [year]}
                selectedCrops={selectedCrops}
                allCropsSentinel={ALL_CROPS_SENTINEL}
                selectedState={selectedState}
                noWrap
            />
        );

        if (attribute === "totalNetFarmerBenefit") {
            return (
                <div>
                    <HighlightPill>
                        <NetFarmerBenefitNote />
                    </HighlightPill>
                    <Box display="flex" justifyContent="center">
                        {selectionTitle}
                    </Box>
                </div>
            );
        }

        if (attribute === "lossRatio") {
            return (
                <div>
                    <HighlightPill fontWeight={600}>{LOSS_RATIO_NOTE}</HighlightPill>
                    <Box display="flex" justifyContent="center">
                        {selectionTitle}
                    </Box>
                </div>
            );
        }

        if (attribute === "averageInsuredAreaInAcres") {
            return (
                <div>
                    <HighlightPill>{AVERAGE_BASIS_NOTE}</HighlightPill>
                    <Box display="flex" justifyContent="center">
                        {selectionTitle}
                    </Box>
                    <PrfAcresCaption />
                </div>
            );
        }
        if (attribute === "averageLiabilities") {
            return (
                <div>
                    <HighlightPill>{AVERAGE_BASIS_NOTE}</HighlightPill>
                    <Box display="flex" justifyContent="center">
                        {selectionTitle}
                    </Box>
                </div>
            );
        }
        return selectionTitle;
    };

    return (
        <div>
            <Box display="flex" justifyContent="center">
                {isLossRatio && (
                    <DrawLegend
                        isRatio
                        ratioAsPercent={false}
                        colorScale={colorScale}
                        title={titleElement()}
                        programData={quantizeArray}
                        prepColor={lossRatioColors}
                        emptyState={zeroPoints}
                        grayAreasSuffix="for acreage-based policies"
                    />
                )}
                {!isLossRatio && mode === "quantile" && (
                    <DrawLegend
                        isRatio={false}
                        notDollar={isCountAttribute}
                        colorScale={colorScale}
                        title={titleElement()}
                        programData={quantizeArray}
                        prepColor={mapColor}
                        emptyState={zeroPoints}
                        useQuantileSpread
                        quantilePercentiles={countyPercentiles}
                        grayAreasSuffix="for acreage-based policies"
                    />
                )}
                {!isLossRatio && mode !== "quantile" && (
                    <SparseCountyLegend
                        title={titleElement()}
                        entries={
                            mode === "sparse" ? getSparseCountyEntries(countyValueMap, countyDataMap, stateCodes) : []
                        }
                        highlightColor={quantileColorScale.highlightColor}
                        emptyMessage={`County data in ${year} is unavailable.`}
                        notDollar={isCountAttribute}
                    />
                )}
            </Box>
            <Box
                sx={{
                    position: "relative",
                    width: "100%"
                }}
            >
                {selectedState !== "All States" && (
                    <Box sx={{ position: "absolute", top: 10, right: 10, zIndex: 2000 }}>
                        <Button
                            onClick={handleCloseStateView}
                            aria-label="return to US map"
                            variant="contained"
                            startIcon={<CloseIcon />}
                            sx={{
                                "bgcolor": BRAND_GREEN_90,
                                "color": "white",
                                "border": "2px solid white",
                                "boxShadow": "0 2px 10px rgba(0,0,0,0.2)",
                                "&:hover": {
                                    bgcolor: BRAND_GREEN,
                                    transform: "scale(1.05)"
                                }
                            }}
                        >
                            Return to US Map
                        </Button>
                    </Box>
                )}
                {!interactionReady && <MapLoadingOverlay label="Rendering county map..." zIndex={1600} />}
                <div
                    data-tip=""
                    data-for="county-map-tooltip"
                    style={{ pointerEvents: interactionReady ? "auto" : "none" }}
                >
                    <ComposableMap projection="geoAlbersUsa">
                        <ZoomableGroup
                            zoom={position.zoom}
                            center={position.coordinates}
                            onMoveEnd={handleMoveEnd}
                            minZoom={0.5}
                            maxZoom={3}
                        >
                            {countyGeographySource && (
                                <Geographies geography={countyGeographySource}>
                                    {({ geographies }) => (
                                        <CountyGeographyLayer
                                            geographies={geographies}
                                            selectedStateFips={selectedStateFips}
                                            getCountyFillColor={getCountyFillColor}
                                            onMouseEnter={handleMouseEnter}
                                            onMouseLeave={handleMouseLeave}
                                            onGeographiesReady={handleCountyTopoReady}
                                        />
                                    )}
                                </Geographies>
                            )}
                            {stateGeographySource && (
                                <Geographies geography={stateGeographySource}>
                                    {({ geographies }) => (
                                        <>
                                            <StateBoundaryLayer
                                                geographies={geographies}
                                                onGeographiesReady={handleStateTopoReady}
                                            />
                                            <StateLabelLayer
                                                geographies={geographies}
                                                selectedState={selectedState}
                                                stateAbbrevByVal={stateAbbrevByVal}
                                            />
                                        </>
                                    )}
                                </Geographies>
                            )}
                        </ZoomableGroup>
                    </ComposableMap>
                </div>
                <Box
                    sx={{
                        position: "absolute",
                        bottom: 10,
                        right: 10,
                        zIndex: 500,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "flex-end",
                        gap: 1
                    }}
                >
                    <Box
                        sx={{
                            fontSize: "11px",
                            color: "#666",
                            backgroundColor: WHITE_90,
                            padding: "4px 8px",
                            borderRadius: "4px",
                            boxShadow: "0 1px 4px rgba(0,0,0,0.1)"
                        }}
                    >
                        Map Zoom Controls
                    </Box>
                    <Box
                        sx={{
                            display: "flex",
                            gap: 1,
                            backgroundColor: WHITE_90,
                            borderRadius: "8px",
                            padding: "8px",
                            boxShadow: "0 2px 8px rgba(0,0,0,0.15)"
                        }}
                    >
                        <Button
                            onClick={handleZoomOut}
                            size="small"
                            variant="outlined"
                            sx={{
                                "minWidth": "auto",
                                "width": "32px",
                                "height": "32px",
                                "borderColor": BRAND_GREEN_50,
                                "color": BRAND_GREEN,
                                "&:hover": {
                                    borderColor: BRAND_GREEN,
                                    backgroundColor: BRAND_GREEN_10
                                }
                            }}
                        >
                            −
                        </Button>
                        <Button
                            onClick={handleResetZoom}
                            size="small"
                            variant="outlined"
                            sx={{
                                "fontSize": "11px",
                                "borderColor": BRAND_GREEN_50,
                                "color": BRAND_GREEN,
                                "&:hover": {
                                    borderColor: BRAND_GREEN,
                                    backgroundColor: BRAND_GREEN_10
                                }
                            }}
                        >
                            Reset
                        </Button>
                        <Button
                            onClick={handleZoomIn}
                            size="small"
                            variant="outlined"
                            sx={{
                                "minWidth": "auto",
                                "width": "32px",
                                "height": "32px",
                                "borderColor": BRAND_GREEN_50,
                                "color": BRAND_GREEN,
                                "&:hover": {
                                    borderColor: BRAND_GREEN,
                                    backgroundColor: BRAND_GREEN_10
                                }
                            }}
                        >
                            +
                        </Button>
                    </Box>
                </Box>
            </Box>
            <div className="tooltip-container">
                <ReactTooltip
                    className={`${classes.customized_tooltip} tooltip`}
                    backgroundColor={tooltipBkgColor}
                    effect="float"
                    clickable={false}
                    offset={{ top: 5, left: 5 }}
                    place="right"
                    id="county-map-tooltip"
                >
                    {content}
                </ReactTooltip>
            </div>
        </div>
    );
};

export default React.memo(CropInsuranceCountyMap);
